import { createReducer, Action, on } from '@ngrx/store';

import { DealFilterActions } from './deal-filter.actions';
import { DealFilterState, DealFilterModePage } from './deal-filter.state';
import { DealFilter, DealFilterMode } from '../../features/deal-filter/models/deal-filter.model';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { AppActions } from '../app/app.actions';

export const DEFAULT_MODE = {
  [DealFilterModePage.DatapointOrganizer]: DealFilterMode.DealFilterOnly,
  [DealFilterModePage.MarketCompare]: DealFilterMode.DealFilterOnly,
  [DealFilterModePage.DealCompare]: DealFilterMode.DealFilterOnly,
  [DealFilterModePage.LlmRule]: DealFilterMode.DealFilterOnly,
};

export const initialState: DealFilterState = {
  loading: false,
  modeLookup: DEFAULT_MODE,
  sort: { active: 'transactionDisplayName', direction: 'asc' },
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      AppActions.LoadInitData,
      (state): DealFilterState => ({ ...state, loading: true, error: undefined })
    ),
    on(
      DealFilterActions.LoadList,
      (state): DealFilterState => ({ ...state, loading: true, error: undefined })
    ),
    on(AppActions.LoadInitDataSuccess, (state, action): DealFilterState => {
      const dealFilterId =
        action.data.assetSettings?.selectedDealFilterId || state.selectedFilterId;
      const filter = action.data.dealFilters?.find((i) => i.id === dealFilterId);
      return {
        ...state,
        loading: false,
        list: action.data.dealFilters,
        error: undefined,
        appliedFilter: filter,
        sort: filter?.sort || state.sort,
        selectedFilterId: filter?.id,
        isBasketScope: filter ? false : state.isBasketScope,
      };
    }),
    on(DealFilterActions.LoadListSuccess, (state, action): DealFilterState => {
      const dealFilterSet = new Map<string, DealFilter>(
        state.list.map((dealFilter) => [dealFilter.id, dealFilter])
      );
      // Only a *saved* selection can go stale — `ApplyFilterSuccess` sets `appliedFilter` with a
      // null `selectedFilterId` for ad-hoc filters, which this must not clear.
      const hasStaleSelection =
        !!state.selectedFilterId && !action.list.some((dealFilter) => dealFilter.id === state.selectedFilterId);

      return {
        ...state,
        loading: false,
        selectedFilterId: hasStaleSelection ? undefined : state.selectedFilterId,
        appliedFilter: hasStaleSelection ? undefined : state.appliedFilter,
        list: action.list.map((dealFilter) => {
          const storedDealFilter = dealFilterSet.get(dealFilter.id);

          // `typeof`, not truthiness: a portfolio resolved to 0 deals must carry that 0 forward, or
          // the refresh drops it and the count falls back to the unresolved `dealList` length.
          if (typeof storedDealFilter?.transactionCount === 'number' && storedDealFilter.transactionIds) {
            return {
              ...dealFilter,
              transactionCount: storedDealFilter.transactionCount,
              transactionIds: storedDealFilter.transactionIds,
            };
          }

          return dealFilter;
        }),
      };
    }),
    on(
      DealFilterActions.SetSort,
      (state, action): DealFilterState => ({ ...state, sort: action.sort })
    ),
    on(DealFilterActions.SetMode, (state, action): DealFilterState => {
      const currentState = state.modeLookup[action.key];

      if (action.condition && currentState !== action.condition) {
        return state;
      }

      return {
        ...state,
        modeLookup: {
          ...state.modeLookup,
          [action.key]: action.mode,
        },
      };
    }),
    on(
      DealFilterActions.CreateOrUpdateDealFilter,
      DealFilterActions.ShareDealFilter,
      (state): DealFilterState => ({ ...state, loading: true })
    ),
    on(DealFilterActions.CreateOrUpdateDealFilterSuccess, (state, action): DealFilterState => {
      const applied = state.appliedFilter;
      const saved = applied?.id === action.id ? action.dealFilters.find((dealFilter) => dealFilter.id === action.id) : undefined;

      return {
        ...state,
        loading: false,
        list: action.dealFilters,
        // Membership stays the applied copy's: the save response need not carry `transactionIds`.
        appliedFilter: saved ? { ...saved, transactionCount: applied.transactionCount, transactionIds: applied.transactionIds } : applied,
      };
    }),
    on(
      DealFilterActions.ShareDealFilterSuccess,
      (state, action): DealFilterState => ({
        ...state,
        loading: false,
        list: state.list.map((filter) =>
          filter.id === action.dealFilter.id ? action.dealFilter : filter
        ),
      })
    ),
    on(DealFilterActions.SetDealMembership, (state, action): DealFilterState => {
      const add = new Set(action.add);
      const remove = new Set(action.remove);

      return {
        ...state,
        list: state.list?.map((dealFilter) => {
          const dealList = dealFilter.dealList ?? [];

          if (add.has(dealFilter.id)) {
            return dealList.includes(action.transactionId)
              ? dealFilter
              : { ...dealFilter, dealList: [...dealList, action.transactionId] };
          }

          if (remove.has(dealFilter.id)) {
            return { ...dealFilter, dealList: dealList.filter((id) => id !== action.transactionId) };
          }

          return dealFilter;
        }),
      };
    }),
    on(DealFilterActions.SetDealMembershipSuccess, (state, action): DealFilterState => {
      const saved = new Map(action.dealFilters.map((dealFilter) => [dealFilter.id, dealFilter]));
      const applied = state.appliedFilter;
      // The local `dealList` is kept: a second edit can be optimistically applied while the first
      // is still saving, and the response carries the membership as it was SENT, which would tick
      // the checkbox back. Everything else on the document is the server's.
      const withSaved = (local: DealFilter): DealFilter => {
        const doc = saved.get(local.id);

        return doc ? { ...doc, dealList: local.dealList } : local;
      };

      return {
        ...state,
        list: state.list?.map(withSaved),
        // Nothing on this path navigates, so no `UseFilter` round-trip re-applies the scope.
        appliedFilter: applied?.id ? withSaved(applied) : applied,
      };
    }),
    on(
      DealFilterActions.LoadListFail,
      DealFilterActions.DeleteFilterFail,
      DealFilterActions.SetDealMembershipFail,
      DealFilterActions.ShareDealFilterFail,
      DealFilterActions.ApplyFilterFail,
      DealFilterActions.UseFilterFail,
      (state, action): DealFilterState => ({ ...state, loading: false, error: action.error })
    ),
    on(
      DealFilterActions.ApplyFilter,
      (state, action): DealFilterState => ({
        ...state,
        loading: true,
      })
    ),
    on(DealFilterActions.ApplyFilterSuccess, (state, action): DealFilterState => {
      return {
        ...state,
        appliedFilter: action.dealFilter,
        loading: false,
        // `list` is the saved copy: the editor's unsaved-changes check compares against it.
        list: action.preview ? state.list : state.list.map((i) => (i.id === action.dealFilter.id ? action.dealFilter : i)),
        selectedFilterId: action.resetSelectedFilter ? null : state.selectedFilterId,
      };
    }),
    on(DealFilterActions.UseFilter, (state, action): DealFilterState => {
      const filter = state.list.find((i) => i.id === action.id);

      return {
        ...state,
        appliedFilter: filter,
        sort: filter?.sort || state.sort,
        selectedFilterId: filter?.id,
        isBasketScope: false,
        // `useFilterQueryEffect$` bails on a falsy id, so arming the flag there sticks for the session.
        loading: !!action.id,
      };
    }),
    // An unsaved preview outlives the page that made it, and would go on filtering behind an All deals picker.
    on(DealFilterActions.UseDefaultFilter, (state): DealFilterState => {
      const isAbandonedPreview = !state.selectedFilterId && !!state.appliedFilter;

      return isAbandonedPreview ? { ...state, appliedFilter: undefined } : state;
    }),
    on(
      DealFilterActions.ClearSelection,
      DealFilterActions.UseBasket,
      (state, action): DealFilterState => ({
        ...state,
        appliedFilter: undefined,
        selectedFilterId: undefined,
        isBasketScope: action.type === DealFilterActions.UseBasket.type,
        loading: false,
      })
    ),
    on(DealFilterActions.UseFilterSuccess, (state, action): DealFilterState => {
      return {
        ...state,
        appliedFilter: action.dealFilter,
        loading: false,
        list: state?.list?.map((i) => (i.id === action.dealFilter.id ? action.dealFilter : i)),
      };
    }),
    on(DealFilterActions.DeleteFilterSuccess, (state, action): DealFilterState => {
      return {
        ...state,
        list: action.dealFilters,
        appliedFilter: null,
        selectedFilterId: null,
      };
    })
  )
);

export function reducer(state: DealFilterState | undefined, action: Action) {
  return featureReducer(state, action);
}
